import { RowIncidenceTimeDto, RowReportIncidenceTimeDto } from "@/dtos";
import { Administrator, IncidenceTime, Identifier} from "@/entities";
import { addTimeToDateTime, getHoursFromTime, parseToLocalDateTimeFromUtc, sumTimes } from "@/helpers/utilities";
import { generateColor } from "@/helpers/utilities/colors";
import { sliceToLength } from "@/helpers/utilities/general";
import { BarDataset } from "@/ui/components/graphs/bar/interfaces";

export const formatRowIncidenceTimeDto = (incidenceTimes: IncidenceTime[]): RowIncidenceTimeDto[] => {
    return incidenceTimes.map(x => {
        const row: RowIncidenceTimeDto = {
            ...x,
            administrator_label: x.details?.administrator,
            date_time_label: parseToLocalDateTimeFromUtc(x.date),
            identifier_label: x.identifier?.name,
            active: x.is_active ? 'Activo' : 'Inactivo'
        }

        return row;
    });
}

export const formatRowReportIncidenceTimeDto = (incidenceTimes: IncidenceTime[]): RowReportIncidenceTimeDto[] => {
    return incidenceTimes.map(x => {
        const hours = parseFloat(getHoursFromTime(x.time_working).toFixed(2));
        const costCenters = x.incidence.incidence_identifiers?.map(x => x.identifier?.name).toString();

        const row: RowReportIncidenceTimeDto = {
            ...x,
            incidence_label: `(${x.incidence.code}) ${x.incidence.name}`,
            administrator_label: x.details?.administrator,
            date_time_label: parseToLocalDateTimeFromUtc(x.date),
            finish_time_label: addTimeToDateTime(parseToLocalDateTimeFromUtc(x.date), hours, 'hours'),
            cost_center_label: x.incidence.incidence_identifiers?.length > 0 ? costCenters : 'S/N',
            identifier_label: x.identifier?.name,
        }

        return row;
    });
}

export const formatGraphReportIncidenceTime = (incidenceTimes: RowReportIncidenceTimeDto[], administrators: Administrator[]) => {
    const labels = [] as string[];
    const datasets = [] as BarDataset[];

    for (const incidenceTime of incidenceTimes) {        
        const label = labels.find(x => x == incidenceTime.incidence.code);
        if (!label) {
            labels.push(incidenceTime.incidence.code);
        }
        const dataset = datasets.find(x => x.label == incidenceTime.administrator_label);
        if (!dataset) {
            const administrator = administrators.find(x => (x.first_name + " " + x.last_name) == incidenceTime.administrator_label);
            const newDataset: BarDataset = {
                label: incidenceTime.administrator_label,
                data: [],
                unit: 'h',
                backgroundColor: [administrator?.color ?? generateColor().color]
            }
            datasets.push(newDataset);
        }
    }

    for (const dataset of datasets) {
        for (const label of labels) {
            const incidenceTimeDatasets = incidenceTimes.filter(x => x.administrator_label == dataset.label && x.incidence.code == label);
            if (incidenceTimeDatasets.length > 0) {
                const times = incidenceTimeDatasets.map(x => x.time_working);
                const hours = parseFloat(getHoursFromTime(sumTimes(times)).toFixed(2));
                dataset.data.push(hours);
                continue;
            }
            dataset.data.push(0);
        }
    }

    return { labels, datasets };
}

export const formatGraphReportIncidenceTimeCenterCost = (incidenceTimes: RowReportIncidenceTimeDto[]) => {
    const labels = [] as string[];
    const datasets = [] as BarDataset[];

    for (const incidenceTime of incidenceTimes) {
        const hours = parseFloat(getHoursFromTime(incidenceTime.time_working).toFixed(2));
        for (const incidenceIdentifier of incidenceTime.incidence.incidence_identifiers) {
            if (incidenceIdentifier.identifier) {
                const label = labels.find(x => x == incidenceIdentifier.identifier.name);
                if (!label) {
                    labels.push(incidenceIdentifier.identifier.name);

                    const dataset: BarDataset = {
                        label: incidenceIdentifier.identifier.name,
                        data: [],
                        unit: 'h',
                        backgroundColor: [incidenceIdentifier.identifier.color]
                    }

                    if (labels.length > 1) {
                        labels.map((x, i) => {
                            dataset.data[i] = (i != (labels.length - 1)) ? 0 : hours;
                        });
                    } else {
                        dataset.data[0] = hours;
                    }

                    datasets.push(dataset);
                }

                const dataset = datasets.find(x => x.label == label);
                if (dataset) {
                    const index = labels.findIndex(x => x == dataset.label);
                    dataset.data.map(x => x = 0);
                    dataset.data[index] += hours;
                }
            }
        }
    }
        
    return { labels, datasets };
}

export const formatGraghReportIncidenceTimeAdministrator = (incidenceTimes: RowReportIncidenceTimeDto[], administrators: Administrator[]) => {
    const labels = [] as string[];
    const datasets = [] as BarDataset[];

    for (const incidenceTime of incidenceTimes) {
        const hours = parseFloat(getHoursFromTime(incidenceTime.time_working).toFixed(2));
        const label = labels.find(x => x == incidenceTime.administrator_label);
        if (!label) {
            labels.push(incidenceTime.administrator_label);
            const administrator = administrators.find(x => (x.first_name + " " + x.last_name) == incidenceTime.administrator_label);
            const dataset: BarDataset = {
                label: incidenceTime.administrator_label,
                data: [],
                unit: 'h',
                backgroundColor: [administrator?.color ?? generateColor().color]
            }

            if (labels.length > 1) {
                labels.map((x, i) => {
                    dataset.data[i] = (i != (labels.length - 1)) ? 0 : hours;
                });
            } else {
                dataset.data[0] = hours;
            }

            datasets.push(dataset);
        }

        const dataset = datasets.find(x => x.label == label);
        if (dataset) {
            const index = labels.findIndex(x => x == dataset.label);
            dataset.data.map(x => x = 0);
            dataset.data[index] += hours;
        }
    }

    return { labels, datasets };
}

export const formatGraphReportIncidenceTimeTaskPercentage = (incidenceTimes: RowReportIncidenceTimeDto[]) => {
    const labels: string[] = [];
    const datasets: BarDataset[] = [];   
    
    const dataset: BarDataset = {
        label:  "Porcentaje trabajado",
        data: [],      
        unit: '%',  
        backgroundColor: [generateColor().color],
    };
    
    const incidences = [...new Map(incidenceTimes.map(x => [x?.incidence.id, x?.incidence])).values()];    
    const totalHours = incidenceTimes
    .map(x => parseFloat(getHoursFromTime(x.time_working).toFixed(2)))
    .reduce((a, b) => a + b, 0);

    for (const i in incidences) {
        const incidence = incidences[i];
        labels.push(sliceToLength(incidence.name, 40));
        const incidenceTime =  incidenceTimes.filter(x=> x?.incidence.id === incidence.id);

        const hours = incidenceTime 
        .map(x => parseFloat(getHoursFromTime(x.time_working).toFixed(2)))
        .reduce((a, b) => a + b, 0);

        const percent = totalHours > 0 ? (hours / totalHours) * 100 : 0.00;                                         
        dataset.data[i] = parseFloat(percent.toFixed(2));     

        dataset.backgroundColor?.push(generateColor().color);
    }
    datasets.push(dataset);
    
    return { labels, datasets };
};

export const formatGraphReportIncidenceNumberTaskPercentage = (incidenceTimes: RowReportIncidenceTimeDto[]) => {
    const labels: string[] = [];
    const datasets: BarDataset[] = [];
    const identifiers = [...new Map(incidenceTimes
                    .filter(x => x?.identifier?.id != null)
                    .map(x => [x.identifier.id, x.identifier]))
                    .values()];      

    for (const i in identifiers) {
        const identifier = identifiers[i];
        const numberTask =  incidenceTimes.filter(x=> x?.identifier?.id === identifier?.id).length;
        const dataset: BarDataset = {
            label: identifier?.name,
            data: [],
            backgroundColor: [identifier?.color ?? generateColor().color],
        };

        labels.push(identifier.name);
        dataset.data[i] = numberTask;        
        datasets.push(dataset);
    }
    
    return { labels, datasets };
};